import { ROWS, COLS, PIECE_COLOR_LIST } from '../../core/constants.js';

const NEUTRAL_COLORS = PIECE_COLOR_LIST.filter((color) => color !== 'red' && color !== 'blue');
const COLOR_SEQUENCE = NEUTRAL_COLORS.length > 0 ? NEUTRAL_COLORS : PIECE_COLOR_LIST;

function baseColorAt(row, col) {
  return COLOR_SEQUENCE[(row + col) % COLOR_SEQUENCE.length];
}

function makeKey(row, col) {
  return `${row}:${col}`;
}

class PresetBuilder {
  constructor() {
    this.overrides = new Map();
  }

  setPiece(row, col, color, specialType = null) {
    this.overrides.set(makeKey(row, col), { row, col, color, specialType });
    return this;
  }

  setSpecial(row, col, color, specialType) {
    return this.setPiece(row, col, color, specialType);
  }

  build() {
    const pieces = [];

    for (let row = 0; row < ROWS; row += 1) {
      for (let col = 0; col < COLS; col += 1) {
        const override = this.overrides.get(makeKey(row, col));
        const color = override?.color ?? baseColorAt(row, col);
        const piece = { row, col, color };
        if (override?.specialType) {
          piece.specialType = override.specialType;
        }
        pieces.push(piece);
      }
    }

    return pieces;
  }
}

function createPreset(configure, meta = {}) {
  const builder = new PresetBuilder();
  if (typeof configure === 'function') {
    configure(builder);
  }
  return {
    ...meta,
    pieces: builder.build(),
  };
}

export const TUTORIAL_PRESETS = Object.freeze({
  match3: createPreset(
    (builder) => {
      builder
        .setPiece(3, 4, 'yellow')
        .setPiece(3, 5, 'red')
        .setPiece(4, 3, 'blue')
        .setPiece(4, 4, 'red')
        .setPiece(4, 5, 'blue')
        .setPiece(5, 4, 'green')
        .setPiece(5, 5, 'red');
    },
    {
      focusCell: { row: 4, col: 4 },
      allowedCells: [
        { row: 4, col: 4 },
        { row: 4, col: 5 },
        { row: 4, col: 3 },
        { row: 3, col: 4 },
        { row: 5, col: 4 },
      ],
      swapConstraints: [
        { from: { row: 4, col: 4 }, to: { row: 4, col: 5 } },
        { from: { row: 4, col: 4 }, to: { row: 4, col: 3 } },
        { from: { row: 4, col: 4 }, to: { row: 3, col: 4 } },
        { from: { row: 4, col: 4 }, to: { row: 5, col: 4 } },
      ],
      completion: {
        size: 3,
        orientation: 'vertical',
        specialCreated: false,
        triggeredSpecial: false,
        mix: false,
      },
    },
  ),
  match4: createPreset(
    (builder) => {
      builder
        .setPiece(1, 5, 'red')
        .setPiece(2, 5, 'blue')
        .setPiece(3, 5, 'blue')
        .setPiece(4, 4, 'blue')
        .setPiece(4, 5, 'green')
        .setPiece(5, 5, 'blue')
        .setPiece(6, 5, 'yellow');
    },
    {
      focusCell: { row: 4, col: 4 },
      allowedCells: [
        { row: 4, col: 4 },
        { row: 4, col: 5 },
      ],
      swapConstraints: [{ from: { row: 4, col: 4 }, to: { row: 4, col: 5 } }],
      completion: {
        size: 4,
        orientation: 'vertical',
        specialCreated: true,
        triggeredSpecial: false,
        mix: false,
      },
    },
  ),
  special: createPreset(
    (builder) => {
      builder
        .setPiece(2, 5, 'blue')
        .setPiece(3, 5, 'blue')
        .setPiece(4, 5, 'green')
        .setPiece(3, 4, 'red')
        .setPiece(5, 4, 'yellow')
        .setSpecial(4, 4, 'blue', 'verticalStripe');
    },
    {
      focusCell: { row: 4, col: 4 },
      allowedCells: [
        { row: 4, col: 4 },
        { row: 4, col: 5 },
      ],
      swapConstraints: [{ from: { row: 4, col: 4 }, to: { row: 4, col: 5 } }],
      completion: {
        triggeredSpecial: true,
        mix: false,
      },
    },
  ),
  mix: createPreset(
    (builder) => {
      builder
        .setSpecial(4, 4, 'special', 'horizontalStripe')
        .setSpecial(4, 5, 'special', 'bomb')
        .setPiece(4, 3, 'purple')
        .setPiece(3, 4, 'yellow')
        .setPiece(5, 4, 'green');
    },
    {
      focusCell: { row: 4, col: 4 },
      allowedCells: [
        { row: 4, col: 4 },
        { row: 4, col: 5 },
      ],
      swapConstraints: [{ from: { row: 4, col: 4 }, to: { row: 4, col: 5 } }],
      completion: {
        mix: true,
      },
    },
  ),
});

export function getTutorialPreset(id) {
  return TUTORIAL_PRESETS[id] ?? null;
}
